import React from "react";
import { Link } from "react-router-dom";
import '../Components/Guides/guides.css'
import Tooltip from '../Components/Guides/Tooltip.jsx'
import BannerCTA from "../Components/CTA/CTABanner.jsx";
import { ArrowUpRight } from "lucide-react";
import BookmakerBanner from "../Components/ui/BookmakerBanner.jsx";
import offersConfig from "./offersConfig.js";
// Mapping object to translate category keys to full titles.
export const categoryMapping = {
  official: { title: "About Matched Betting", priority: 1 },
  howItWorks: { title: "How Matched Betting Works", priority: 2 },
  offerTypes: { title: "Offer Types & Promotions", priority: 3 },
  tools: { title: "Tools and Tracking", priority: 4 },
};

const faqConfig = {
  cancellingFreeBet: {
    pages: ["getStarted", "faqs"],
    priority: 6,
    category: "howItWorks",
    title: "How does cancelling out a free bet guarantee a profit?",
    content: (
      <>
        <p>
          When you get a free bet, it’s the bookmaker’s money, not yours. You place a <strong className="back">back bet</strong> using that free bet, then place a <strong className="lay">lay bet</strong> at a betting exchange using your own money. The <strong className="back">back</strong> bet wins if your team wins; the <strong className="lay">lay</strong> bet wins if they don’t.
        </p>
        <p>
          Because you’ve covered all outcomes, you don’t risk losing anything. You don’t get the free bet stake back, but you do get the winnings — and by laying correctly, you keep most of that as profit. You only lose a bit to <strong>exchange commission</strong> or small <strong>lay losses</strong>, which are taken into account when calculating your lay stake.
        </p>
        <p>
          Even though you’re laying with real money, your lay bet only <em>loses</em> if the free bet <em>wins</em> — and when that happens, you’ve gained from the bookmaker’s money. That’s how the maths works out in your favour.
        </p>
      </>
    ),
  },


  freeBetValue: {
    pages: ["getStarted", "faqs"],
    priority: 4,
    category: "howItWorks",
    title: "Why do you only keep 70–80% of a free bet's value?",
    content: (
      <>
        <p>
          When you place a normal bet and it wins, you get your <span className="key">stake</span> back plus the <span className="key">winnings</span>. But with a <strong>free bet</strong>, you only get the winnings — the stake belongs to the bookie, so it’s not returned.
        </p>
        <p>
          That means even before you do anything else, the free bet is worth less than face value. A £10 free bet at odds of 2.0 only returns £10 profit — not £20 — because the stake isn’t included.
        </p>
        <p>
          Let’s say you have a <strong>£10 free bet</strong> and use it on something with odds of <strong>5.0</strong>. If it wins:
        </p>
        <ul>
          <li>Normal bet with your own money: £10 back (stake) + £40 profit = £50 total return.</li>
          <li>Free bet: £0 stake back + £40 profit = £40 total return.</li>
        </ul>
        <p>
          So straight away, the free bet is worth less than a real £10 bet — because you lose the stake. That’s why it can never return the full £10 as profit.
        </p>
        <p>
          Now let’s say you <strong className="lay">lay</strong> that free bet at the exchange to lock in a profit. You’ll lose a small amount (maybe £20p) in the process due to slightly worse lay odds and <strong>exchange commission</strong>.
        </p>
        <p>
          Final result: You walk away with <strong>£7.80 guaranteed profit</strong> from a £10 free bet — within the expected range of about <strong>70–80%</strong> of its face value.
        </p>
        <p>
          This is completely normal. The missing stake is the main reason you can’t extract the full £10.
        </p>
      </>
    ),
  },

  legal: {
    pages: ["getStarted", "faqs"],
    priority: 8,
    category: "official",
    title: "Is matched betting legal?",
    content: (
      <>
        <p>
          Yes, matched betting is <a href="https://www.oddsmonkey.com/blog/matched-betting/is-matched-betting-legal/" target="_blank" rel="noreferrer">completely legal</a> in the UK. You’re just using bookmaker offers in a smart way.
        </p>
      </>
    ),
  },

  risks: {
    pages: ["getStarted", "faqs"],
    priority: 7,
    category: "official",
    title: "What risks are there with matched betting?",
    content: (
      <>
        <p>
          Matched betting can be <strong>low-risk</strong>, but it’s not <strong>no-risk</strong>. The biggest danger is making a mistake — entering the wrong odds, laying the wrong selection, or missing a rule in the offer.
        </p>
        <p>
          Done perfectly, the maths removes risk. But humans aren't perfect.
        </p>
        <p>
          We’ve written a full guide on this — give it a read.
        </p>
        <BannerCTA 
          to="/guides/matched-betting-risk-free-guide" 
          text="Read all about why matched betting isn’t risk-free" 
          icon={ArrowUpRight} 
        />
      </>
    ),
  },

  startingCapital: {
    pages: ["getStarted", "faqs"],
    priority: 3,
    category: "official",
    title: "How much money do I need to start matched betting?",
    content: (
      <>
        <p>
          You ideally need <strong>£100</strong> to start, and an <strong>absolute minimum of £50</strong>.
        </p>
        <p>
          This covers the <Tooltip content="The amount you risk losing when placing a lay bet">liability</Tooltip> on your <strong className="lay">lay</strong> bets — money held by the exchange in case your <strong className="lay">lay</strong> bet loses. Once you complete a few offers and start turning a profit, you can recycle that money into more bets.
        </p>
        <p>
          Start small, learn the ropes, and build from there.
        </p>
      </>
    ),
  },

  offers: {
    pages: ["getStarted", "faqs"],
    priority: 2,
    category: "offerTypes",
    title: "Where can I find reliable free bet offers?",
    content: (
      <>
        <p>A few easy ways:</p>
          <p>
            Sign up to popular UK bookies — most have <strong>sign-up offers</strong>.
          </p>
          <div>
            {offersConfig.bookies.map((offer, index) => (
              <BookmakerBanner key={index} {...offer} />
            ))}
            </div>
          <p>
            Use matched betting platforms. They list offers and explain how to complete them step by step.
          </p>
          <p>
            We recommend OddsMonkey. <Link to={"/guides/oddsmonkey-guide"}>Learn why.</Link>
          </p>
      </>
    ),
  },

  trackingBets: {
    pages: ["faqs"],
    priority: 1,
    category: "tools",
    title: "What’s the best way to track my bets and calculate my profit?",
    content: (
      <>
        <p>
          Many people start with a <strong>spreadsheet</strong>. Log the bookie, bet type, odds, stakes, and result. This helps you spot errors and stay on top of your <strong>bankroll</strong>.
        </p>
        <p>
        <Link to={"/guides/oddsmonkey-guide"}>OddsMonkey</Link> includes a built-in <strong>profit tracker</strong>, which does the work for you. Either way, tracking your bets keeps you organised and shows exactly how much you’re making — offer by offer.
        </p>
      </>
    ),
  },
  matchedBettingProfitsTaxable: {
    pages: ["faqs"],
    priority: 8,
    category: "official",
    title: "Are matched betting profits taxable?",
    content: (
      <>
        <p>
          Matched betting profits are <a href="https://sdlccorp.com/post/what-are-the-tax-implications-of-online-gambling-winnings-in-the-united-kingdom/" target="_blank" rel="noreferrer">tax-free</a> in the UK.
        </p>
      </>
    ),
  },

  layLiabilityCalc: {
    pages: ["faqs"],
    priority: 2,
    category: "howItWorks",
    title: "What is liability and how do I calculate it?",
    content: (
      <>
        <p>
          Lay liability is the amount you would pay out to your backer on the betting exchange if you lose, a <span className="lay">lay</span> bet.
        </p>
        <pre>
          <code>Liability = Stake * (Lay Odds - 1)</code>
        </pre>
        <p>
          Matched betting calculators do the work for you. Plug in the numbers, and you’ll know exactly how much you’re liable for if your lay bet loses.
        </p>
        <BannerCTA 
        to="/calculators/lay-stake-calculator"
        text="Try the Lay Stake Calculator"
        icon={ArrowUpRight}
      />
      </>
    ),
  },

  bookmakerAccountLimited: {
    pages: ["faqs"],
    priority: 4,
    category: "official",
    title: "What is 'gubbing'?",
    content: (
      <>
       <h2>What is "gubbing"?</h2>
  <p>
    Gubbing is when a bookmaker starts to restrict your account, usually after noticing a pattern of matched betting activity. Essentially, the bookmaker recognises you as a savvy user and then limits your access to certain offers, bonuses, or stakes as a way of protecting their own profits.
  </p>
  
  <h3>It's normal</h3>
  <p>
    If you’re matched betting, gubbing is something you’ll almost certainly encounter at some point. Bookmakers set these restrictions as part of their regular risk management, so it’s a normal and expected part of the matched betting experience.
  </p>
  
  <h3>What to do</h3>
  <p>
    There isn’t much you can do to prevent gubbing entirely. When one account gets gubbed, simply move on to another account or bookmaker and keep making the most of new offers. There are also advanced techniques you can use to keep profiting. Remember, gubbing is just part and parcel of matched betting.
  </p>
      </>
    ),
  },

  freeBetOdds: {
    pages: ["getStarted", "faqs"],
    priority: 5,
    category: "howItWorks",
    title: "Why do free bets need to be used on higher odds?",
    content: (
      <>
        <p>
          Free bets don’t give you the stake back — only the winnings. So if you place a £10 free bet at odds of 1.2 (1/5), you only make £2 before costs.
        </p>
        <p>
          But with higher odds, you get more back from the same free bet. For example, odds of 5.0 would return £40 from a £10 free bet. After laying, you keep around 70–80% of that as profit.
        </p>
        <p>
          That’s why matched bettors often look for odds between 5.0 and 7.0 — it’s the sweet spot where your <strong>lay liability</strong> stays manageable and the return is high enough to make the offer worth doing.
        </p>
      </>
    ),
  },

  matchedBettingOnMobile: {
    pages: ["faqs"],
    priority: 7,
    category: "official",
    title: "Can I matched bet using my phone?",
    content: (
      <>
        <p>
          Absolutely. Most matched betting websites and betting exchange apps work well on modern smartphones. Whether you’re at home or on the move, using your mobile gives you the flexibility to check offers, run calculators, and place bets conveniently. It's just a bit more fiddly moving between screens.
        </p>
      </>
    ),
  },

  whatIsLayBet: {
    pages: ["faqs"],
    priority: 8,
    category: "howItWorks",
    title: "What exactly is a lay bet?",
    content: (
      <>
        <p>
          A lay bet is a bet placed on a betting exchange that an event <strong>will not</strong> happen. This is a key component of matched betting as it allows you to cancel out the free bet across all possible outcomes.
        </p>
        <BannerCTA 
          to="/guides/lay-bet-guide" 
          text="Learn how to lay bets" 
          icon={ArrowUpRight} 
        />
      </>
    ),
  },

  whyBookmakersOfferFreeBets: {
    pages: ["faqs"],
    priority: 9,
    category: "offerTypes",
    title: "Why do bookmakers offer free bets and bonuses?",
    content: (
      <>
        <p>
          Bookmakers use free bets and bonuses to attract new customers and keep existing ones coming back. These promotions are designed as incentives, and when used with matched betting techniques, they provide a way to generate profit.
        </p>
      </>
    ),
  },

  reinvestMatchedBettingProfits: {
    pages: ["faqs"],
    priority: 10,
    category: "official",
    title: "Can I reinvest my matched betting profits?",
    content: (
      <>
        <p>
          Yes, reinvesting your profits is a common strategy among matched bettors. Once you’ve built up a profit, you can use that money to complete new offers. This helps to grow your bankroll.
        </p>
      </>
    ),
  },

};

export default faqConfig;